import { type FlagCondition, type FlagEvalContext, matches } from 'ProdantixSdk/core/predicate';

export interface FlagVariation {
  key: string;
  value: unknown;
  weight: number;
}

// A flag this flag requires to serve a named variation before its own rules
// run. Evaluated identically in every evaluator, against the sibling rules of
// the same snapshot.
export interface FlagPrerequisite {
  flagKey: string;
  variationKey: string;
}

export interface FlagRule {
  description?: string;
  enabled: boolean;
  intent?: string;
  key: string;
  prerequisites?: FlagPrerequisite[];
  rolloutPercentage: number;
  targeting: FlagCondition[];
  type?: string;
  variations?: FlagVariation[];
}

export interface FlagSnapshot {
  flags: FlagRule[];
  generatedAt: string;
  // The realtime gateway a server SDK streams changes from; absent when the
  // deployment has none, in which case the poll is the only refresh.
  socketUrl?: string;
}

export interface FlagDecision {
  enabled: boolean;
  reason: string;
}

// FNV-1a over the UTF-16 code units of `"{key}:{distinctId}"`, 32-bit wrapping
// (`Math.imul`), reduced to `0..100`. Byte-exact with the server's `bucket`
// (`charCodeAt` ↔ `encode_utf16`, `Math.imul` ↔ `wrapping_mul`), so a user lands
// in the same rollout bucket on either side.
export function bucket(key: string, distinctId: string): number {
  const subject = `${key}:${distinctId}`;
  let hash = 2166136261;
  for (let i = 0; i < subject.length; i++) {
    hash ^= subject.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % 100;
}

function targetingMatches(rule: FlagRule, ctx: FlagEvalContext): boolean {
  return rule.targeting.length === 0 || rule.targeting.every(condition => matches(condition, ctx));
}

// The variation the bucket alone assigns, ignoring enabled, targeting and
// prerequisites: what a prerequisite check compares against. Bucket ranges are
// allocated from the END of the variations list (the last variation starts at
// 0), so a backfilled boolean flag's `true` variation covers exactly the ids
// the legacy rollout served; a boolean flag with no variations answers
// `true`/`false` from its rollout the same way.
function rawAssignment(rule: FlagRule, ctx: FlagEvalContext): string {
  const assigned = bucket(rule.key, ctx.distinctId);
  const variations = rule.variations ?? [];
  if (variations.length === 0) {
    return rule.rolloutPercentage >= 100 || assigned < rule.rolloutPercentage ? 'true' : 'false';
  }
  let cumulative = 0;
  for (let index = variations.length - 1; index >= 0; index--) {
    cumulative += variations[index].weight;
    if (assigned < cumulative) {
      return variations[index].key;
    }
  }
  return variations[0].key;
}

// A prerequisite is satisfied when the named flag is in the snapshot, enabled,
// its own prerequisites hold, its targeting matches, and its bucket assigns the
// required variation. `visited` carries the chain so a cycle evaluates as
// unsatisfied instead of looping.
function prerequisitesSatisfied(rule: FlagRule, rules: FlagRule[], ctx: FlagEvalContext, visited: string[]): boolean {
  for (const prerequisite of rule.prerequisites ?? []) {
    if (visited.includes(prerequisite.flagKey)) {
      return false;
    }
    const parent = rules.find(candidate => candidate.key === prerequisite.flagKey);
    if (!parent || !parent.enabled) {
      return false;
    }
    visited.push(parent.key);
    const satisfied = prerequisitesSatisfied(parent, rules, ctx, visited) && targetingMatches(parent, ctx) && rawAssignment(parent, ctx) === prerequisite.variationKey;
    visited.pop();
    if (!satisfied) {
      return false;
    }
  }
  return true;
}

// Mirrors the server's `evaluate_flag_in`: disabled short-circuits; every
// prerequisite must hold; every targeting condition must match; a full rollout
// is on; otherwise the bucket decides.
export function evaluateFlagIn(rule: FlagRule, rules: FlagRule[], ctx: FlagEvalContext): FlagDecision {
  if (!rule.enabled) {
    return { enabled: false, reason: 'disabled' };
  }
  if (!prerequisitesSatisfied(rule, rules, ctx, [rule.key])) {
    return { enabled: false, reason: 'prerequisite' };
  }
  if (!targetingMatches(rule, ctx)) {
    return { enabled: false, reason: 'targeting' };
  }
  if (rule.rolloutPercentage >= 100) {
    return { enabled: true, reason: 'rollout_full' };
  }
  if (bucket(rule.key, ctx.distinctId) < rule.rolloutPercentage) {
    return { enabled: true, reason: 'rollout' };
  }
  return { enabled: false, reason: 'rollout_excluded' };
}

// `evaluateFlagIn` with no siblings: a flag with prerequisites cannot be
// satisfied here, which is the fail-closed answer.
export function evaluateFlag(rule: FlagRule, ctx: FlagEvalContext): FlagDecision {
  return evaluateFlagIn(rule, [], ctx);
}

export function evaluateAll(rules: FlagRule[], ctx: FlagEvalContext): Record<string, boolean> {
  const decisions: Record<string, boolean> = {};
  for (const rule of rules) {
    decisions[rule.key] = evaluateFlagIn(rule, rules, ctx).enabled;
  }
  return decisions;
}

// Mirrors the server's `assign_variant_in`. Disabled flags, unsatisfied
// prerequisites and targeting misses return the first variation (the off
// state); a flag without variations assigns nothing.
export function assignVariantIn(rule: FlagRule, rules: FlagRule[], ctx: FlagEvalContext): FlagVariation | null {
  const variations = rule.variations ?? [];
  const first = variations[0];
  if (!first) {
    return null;
  }
  if (!rule.enabled) {
    return first;
  }
  if (!prerequisitesSatisfied(rule, rules, ctx, [rule.key]) || !targetingMatches(rule, ctx)) {
    return first;
  }
  const assigned = rawAssignment(rule, ctx);
  return variations.find(variation => variation.key === assigned) ?? first;
}

export function assignVariant(rule: FlagRule, ctx: FlagEvalContext): FlagVariation | null {
  return assignVariantIn(rule, [], ctx);
}

// Every flag's assigned variation. A boolean flag without variations has no
// stored variation to hand back, so its `true`/`false` decision is synthesised
// as one: the shape the golden vectors report.
export function assignAll(rules: FlagRule[], ctx: FlagEvalContext): Record<string, FlagVariation> {
  const assigned: Record<string, FlagVariation> = {};
  for (const rule of rules) {
    const variation = assignVariantIn(rule, rules, ctx);
    if (variation) {
      assigned[rule.key] = variation;
    } else {
      const enabled = evaluateFlagIn(rule, rules, ctx).enabled;
      assigned[rule.key] = { key: String(enabled), value: enabled, weight: 0 };
    }
  }
  return assigned;
}

// Mirrors the server contract: the assigned variation's VALUE, or null when the
// flag is unknown or carries no variations.
export function getVariant(rules: FlagRule[], key: string, ctx: FlagEvalContext): unknown {
  const rule = rules.find(candidate => candidate.key === key);
  if (!rule) {
    return null;
  }
  return assignVariantIn(rule, rules, ctx)?.value ?? null;
}

// Whether any rule reads cohort membership, which is what decides whether a
// local evaluator must fetch the distinct id's memberships at all.
export function snapshotNeedsCohorts(rules: FlagRule[]): boolean {
  return rules.some(rule => rule.targeting.some(condition => condition.op === 'in_cohort' || condition.op === 'not_in_cohort'));
}
